import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Bot,
  ChevronDown,
  ChevronUp,
  Copy,
  Database,
  HelpCircle,
  LayoutGrid,
  Loader2,
  Plus,
  Save,
  Search,
  Sparkles,
  Trash2,
  TriangleAlert,
  Upload,
  UserCheck,
  Workflow,
} from 'lucide-react';

import {
  complaintTicketsAPI,
  digitalCompanionDatasetsAPI,
  deliveriesAPI,
} from '../../services/api';
import { cn } from '../../lib/utils';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { toast } from '../ui/use-toast';

type ConfigResponse = {
  enabled: boolean;
  digital_companion_enabled: boolean;
  config: Record<string, any>;
  agent_notify_template_id?: number | null;
  notify_channel_id?: number | null;
};

type Question = { id: string; en: string };
type Category = {
  id: string;
  labels?: { en?: string };
  persona_name?: string;
  // §3.8: differentiates a category's tone/voice beyond just its name —
  // threaded into the counselling-turn system prompt in
  // digital_companion_service.py._counselling_response.
  persona_description?: string;
  // Admin-facing description of what this category is for — shown in the
  // Configured Categories grid/edit panel and synced into the matching
  // Indicator Catalogue row's own `description` (ensure_indicator_from_category,
  // ticketing_service.py) when that feature is enabled. Purely informational —
  // never read by classification (_classify_category only ever sees `labels`).
  description?: string;
  specialty?: { en?: string };
  resolution_mode?: 'human' | 'virtual_close';
  questions?: Question[];
  // Categories auto-created from the Indicator Catalogue start inactive —
  // unreachable by real classification until reviewed and activated here.
  // Missing key (hand-authored / default-seeded categories) counts as active.
  active?: boolean;
  // 2026-08-07: opt-in per digital agent. Both apply to the counselling
  // conversation (Virtual close categories) — the one place Digital
  // Companion generates a free-form LLM reply; the intake question flow is
  // scripted and doesn't use either. Missing key = off.
  use_ragflow?: boolean;
  use_mcp_tools?: boolean;
  // 2026-08-08 (§4.6): default priority a ticket gets on creation from this
  // category — read by ticketing_service.py's create_ticket. Missing key =
  // "normal", matching the backend's own fallback.
  default_priority?: 'low' | 'normal' | 'high' | 'critical';
  // §4.2: SLA target for tickets created from this category, in minutes.
  // Missing = no SLA clock for this category (opt-in, not forced).
  sla_response_minutes?: number;
  // 2026-08-08 (§5): default false — a contact who already has an open
  // ticket in this category is prompted to add to it instead of silently
  // creating a duplicate. Missing key = blocked, same fail-safe-default
  // convention as `active`/empty assignee pools.
  allow_multiple_open?: boolean;
  // 2026-08-09: category-level notification override. Superseded by the
  // single account-wide AgentNotificationSettings block above — the fields
  // are no longer editable here (2026-08-25 simplification: "notification
  // channel/template can be the same for all"). Left in the type only so
  // older saved configs that still carry a value don't throw on load; the
  // backend precedence chain (rule > category > account) is unaffected by
  // this UI simply never writing to it again.
  notification_template_id?: number;
  notification_channel_id?: number;
  // Docs/SERVICE_PACKAGE_AI_COUNSELLING_PIPELINE_PLAN.md — which Service
  // Package type this category's completions hand off to: the pre-existing
  // auto-create-a-delivery-on-completion behavior
  // (_maybe_create_session_delivery, either resolution mode) AND the newer
  // "Start AI session" button on /dashboard/packages (virtual_close only —
  // that one primes a conversation *into* this category). Missing key =
  // neither applies; this category has no linked package type.
  session_delivery_type_id?: number;
};

type DeliveryTypeOption = { id: number; label: string; archetype: string };

type DatasetItem = {
  id: number;
  category_id: string;
  title: string;
  content: any;
  source: string;
};

// 2026-08-07: French copy dropped on load, not just hidden — same call
// already made for category labels/questions/specialty. digital_companion_service.py's
// _copy() already falls back to English whenever a language's entry is
// missing, so a French-speaking user was never actually blocked by this —
// but leaving stale, unmaintained French text sitting in the config risked
// drifting out of sync with English edits made after this point, since
// there's no UI left to keep it updated. Dropped outright instead.
const editableProductConfig = (config: Record<string, any> = {}) => {
  const editable = { ...config };
  delete editable.digital_companion_enabled;
  if (editable.copy) {
    editable.copy = { en: editable.copy.en || {} };
  }
  return editable;
};

const uniqueId = (base: string, existing: string[]): string => {
  if (!existing.includes(base)) return base;
  let i = 2;
  while (existing.includes(`${base}_${i}`)) i += 1;
  return `${base}_${i}`;
};

const newQuestionId = (existing: Question[]): string =>
  uniqueId('question', existing.map((q) => q.id));

const slugify = (label: string): string =>
  label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

// Every category is always run by its Digital Companion persona — that part
// never changes. "Resolution mode" only decides what happens once intake is
// done: the persona hands off to a person on your team, or it continues the
// conversation itself and can close the ticket directly. These three helpers
// keep that distinction consistent everywhere it's shown (grid cards, the
// edit panel, and the add-new fork) instead of the old "Human · Tomas" /
// "AI · Tomas" phrasing, which read as if the persona itself was a human.
const modeLabel = (mode?: string): string =>
  mode === 'virtual_close' ? 'Companion resolves it directly' : 'Hands off to a person';

const detectLine = (category: Category): string => {
  const priority = category.default_priority
    ? category.default_priority.charAt(0).toUpperCase() + category.default_priority.slice(1)
    : 'Normal';
  return category.sla_response_minutes
    ? `${priority} priority, ${category.sla_response_minutes} min SLA`
    : `${priority} priority, no SLA`;
};

const askLine = (category: Category): string => {
  const count = (category.questions || []).length;
  return `${count} intake question${count === 1 ? '' : 's'}`;
};

const resolveLine = (category: Category): string => {
  const persona = category.persona_name?.trim() || 'Your companion';
  return category.resolution_mode === 'virtual_close'
    ? `${persona} closes it directly`
    : `${persona} hands off to a person`;
};

type NewCategoryDraft = {
  label: string;
  description: string;
  personaName: string;
  mode: 'human' | 'virtual_close';
  priority: Category['default_priority'];
  slaMinutes: string;
  allowMultipleOpen: boolean;
  questions: string[];
  personaDescription: string;
  sessionDeliveryTypeId: string;
  useRagflow: boolean;
  useMcpTools: boolean;
  hint: string;
};

const createBlankDraft = (): NewCategoryDraft => ({
  label: '',
  description: '',
  personaName: '',
  mode: 'human',
  priority: 'normal',
  slaMinutes: '',
  allowMultipleOpen: false,
  questions: [''],
  personaDescription: '',
  sessionDeliveryTypeId: 'none',
  useRagflow: false,
  useMcpTools: false,
  hint: '',
});

interface DigitalCompanionSettingsProps {
  // 2026-08-09: set from SettingsPage's `?category=` query param so the
  // Indicator Catalogue's "configure this category" deep link lands
  // expanded and scrolled to the right card, instead of the admin having
  // to find it themselves — same highlight-on-deep-link idiom as
  // IssuedTicketsPanel's `highlightTicketNumber`.
  highlightCategoryId?: string | null;
}

const DigitalCompanionSettings: React.FC<DigitalCompanionSettingsProps> = ({
  highlightCategoryId,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [value, setValue] = useState<ConfigResponse | null>(null);
  const [jsonText, setJsonText] = useState('');
  const navigate = useNavigate();
  const configuredCardRef = useRef<HTMLDivElement | null>(null);
  const highlightHandledRef = useRef<string | null>(null);
  const [deliveryTypes, setDeliveryTypes] = useState<DeliveryTypeOption[]>([]);
  const [datasets, setDatasets] = useState<Record<string, DatasetItem[]>>({});
  const [datasetsLoading, setDatasetsLoading] = useState<Record<string, boolean>>({});
  const [datasetUploading, setDatasetUploading] = useState<Record<string, boolean>>({});
  const [personaSuggesting, setPersonaSuggesting] = useState<Record<string, boolean>>({});
  const [questionsSuggesting, setQuestionsSuggesting] = useState<Record<string, boolean>>({});
  const datasetFileInputs = useRef<Record<string, HTMLInputElement | null>>({});

  // 2026-08-25: replaces the old always-expanded accordion list of
  // categories (each with ~10 fields on screen at once) with a
  // library-grid-plus-drill-in shell, built from stakeholder mockups
  // v31 (Configured library / Add-new split) and v17 (mode-first branching
  // for new categories) — see Docs/mockup/digital-companion-settings/.
  const [catView, setCatView] = useState<'grid' | 'add'>('grid');
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [newCategory, setNewCategory] = useState<NewCategoryDraft>(createBlankDraft());
  const [draftingNew, setDraftingNew] = useState(false);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [config, deliveryTypeOptions] = await Promise.all([
        complaintTicketsAPI.getConfig(),
        // Docs/SERVICE_PACKAGE_AI_COUNSELLING_PIPELINE_PLAN.md — fetched
        // separately rather than folded into getConfigOptions, so this
        // page doesn't need a backend change just to list package types
        // it already has a working endpoint for.
        deliveriesAPI.listDeliveryTypes(true).catch(() => []),
      ]);
      setValue({
        ...config,
        digital_companion_enabled: config.digital_companion_enabled ?? true,
      });
      setJsonText(JSON.stringify(editableProductConfig(config.config), null, 2));
      setDeliveryTypes(deliveryTypeOptions || []);
    } catch (error: any) {
      toast({
        title: 'Could not load Digital Companion settings',
        description: error?.response?.data?.detail || 'Administrator access is required.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const parsedResult = useMemo(() => {
    try {
      const parsed = JSON.parse(jsonText);
      if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
        throw new Error('Configuration must be a JSON object');
      }
      return { parsed, error: null as string | null };
    } catch (error: any) {
      return { parsed: null, error: error.message || 'Invalid JSON' };
    }
  }, [jsonText]);
  const parsedConfig = parsedResult.parsed;
  const jsonError = parsedResult.error;

  const categories: Category[] = parsedConfig?.categories || [];
  const categoryIds = categories.map((c) => c.id);
  const filteredCategories = categorySearch.trim()
    ? categories.filter((c) => {
        const q = categorySearch.trim().toLowerCase();
        return (
          (c.labels?.en || c.id).toLowerCase().includes(q) ||
          (c.persona_name || '').toLowerCase().includes(q)
        );
      })
    : categories;

  // Runs once per distinct highlightCategoryId once that category has
  // actually loaded (categoryIds is empty until the initial `load()`
  // resolves) — opens it directly in the edit panel and scrolls the
  // Configured Categories card into view. Guarded by highlightHandledRef so
  // re-renders (e.g. from editing a field) don't keep re-forcing it open
  // after the admin navigates back to the grid.
  useEffect(() => {
    if (!highlightCategoryId) return;
    if (highlightHandledRef.current === highlightCategoryId) return;
    if (!categoryIds.includes(highlightCategoryId)) return;
    highlightHandledRef.current = highlightCategoryId;
    setCatView('grid');
    setEditingCategoryId(highlightCategoryId);
    requestAnimationFrame(() => {
      configuredCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightCategoryId, categoryIds.join(',')]);

  // All structured edits funnel through here so the raw JSON textarea below
  // stays the single source of truth in both directions — editing a category
  // field rewrites jsonText, editing jsonText re-derives the category list.
  const updateConfig = (mutator: (cfg: Record<string, any>) => Record<string, any>) => {
    if (!parsedConfig) return; // JSON currently invalid — fix it below first
    const next = mutator(parsedConfig);
    setJsonText(JSON.stringify(next, null, 2));
  };

  const updateCategories = (mutator: (cats: Category[]) => Category[]) => {
    updateConfig((cfg) => ({ ...cfg, categories: mutator(cfg.categories || []) }));
  };

  const updateCategory = (id: string, patch: Partial<Category>) => {
    updateCategories((cats) =>
      cats.map((c) => (c.id === id ? { ...c, ...patch } : c))
    );
  };

  const duplicateCategory = (source: Category) => {
    const id = uniqueId(`${source.id}_copy`, categoryIds);
    updateConfig((cfg) => {
      const cats: Category[] = cfg.categories || [];
      const idx = cats.findIndex((c) => c.id === source.id);
      const clone: Category = { ...JSON.parse(JSON.stringify(source)), id };
      const nextCats = [...cats];
      nextCats.splice(idx + 1, 0, clone);
      const sourceKeywords = ((cfg.detection || {}).keywords || {})[source.id] || [];
      return {
        ...cfg,
        categories: nextCats,
        detection: {
          ...(cfg.detection || {}),
          keywords: {
            ...((cfg.detection || {}).keywords || {}),
            [id]: [...sourceKeywords],
          },
        },
      };
    });
  };

  const deleteCategory = (id: string) => {
    updateConfig((cfg) => {
      const target: Category | undefined = (cfg.categories || []).find((c: Category) => c.id === id);
      const existingKeywords: string[] = ((cfg.detection || {}).keywords || {})[id] || [];
      const nextKeywords = { ...((cfg.detection || {}).keywords || {}) };
      delete nextKeywords[id];

      // Tombstone: if this still looks like an untouched auto-created stub
      // (never activated, no keywords, no questions added), remember its id
      // so a later, unrelated edit on the same indicator doesn't silently
      // recreate what was just deliberately deleted. Deleting a category an
      // admin has already customized doesn't tombstone — it's no longer
      // indicator-driven at that point, so there's nothing to protect.
      const isUntouchedStub =
        !!target &&
        target.active === false &&
        (target.questions || []).length === 0 &&
        existingKeywords.length === 0;
      const dismissed: string[] = cfg.dismissed_indicator_categories || [];

      return {
        ...cfg,
        categories: (cfg.categories || []).filter((c: Category) => c.id !== id),
        detection: { ...(cfg.detection || {}), keywords: nextKeywords },
        dismissed_indicator_categories: isUntouchedStub
          ? Array.from(new Set([...dismissed, id]))
          : dismissed,
      };
    });
  };

  const moveCategory = (id: string, direction: -1 | 1) => {
    updateCategories((cats) => {
      const idx = cats.findIndex((c) => c.id === id);
      const target = idx + direction;
      if (idx < 0 || target < 0 || target >= cats.length) return cats;
      const next = [...cats];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const addQuestion = (categoryId: string) => {
    updateCategories((cats) =>
      cats.map((c) => {
        if (c.id !== categoryId) return c;
        const questions = c.questions || [];
        return {
          ...c,
          questions: [...questions, { id: newQuestionId(questions), en: '' }],
        };
      })
    );
  };

  const updateQuestion = (categoryId: string, index: number, patch: Partial<Question>) => {
    updateCategories((cats) =>
      cats.map((c) => {
        if (c.id !== categoryId) return c;
        const questions = [...(c.questions || [])];
        questions[index] = { ...questions[index], ...patch };
        return { ...c, questions };
      })
    );
  };

  const removeQuestion = (categoryId: string, index: number) => {
    updateCategories((cats) =>
      cats.map((c) => {
        if (c.id !== categoryId) return c;
        const questions = [...(c.questions || [])];
        questions.splice(index, 1);
        return { ...c, questions };
      })
    );
  };

  const moveQuestion = (categoryId: string, index: number, direction: -1 | 1) => {
    updateCategories((cats) =>
      cats.map((c) => {
        if (c.id !== categoryId) return c;
        const questions = [...(c.questions || [])];
        const target = index + direction;
        if (target < 0 || target >= questions.length) return c;
        [questions[index], questions[target]] = [questions[target], questions[index]];
        return { ...c, questions };
      })
    );
  };

  // Builds a brand-new category from the Add New form and saves it
  // immediately — previously this only appended to local state and relied
  // on the admin remembering to separately click "Save Feature Settings"
  // afterward; confirmed live that a category added this way vanished on
  // refresh whenever that second click didn't happen (easy to miss — it's
  // a whole separate button at the bottom of the page). Building `nextConfig`
  // directly here (rather than going through updateCategories'/setJsonText's
  // state indirection and then calling save()) avoids reading a stale
  // pre-update parsedConfig from this render's closure.
  const handleCreateCategory = async () => {
    if (!newCategory.label.trim()) {
      toast({
        title: 'Give it a label',
        description: 'Name the category before adding it.',
        variant: 'destructive' as any,
      });
      return;
    }
    if (!parsedConfig) {
      toast({
        title: 'Fix the configuration first',
        description: 'The JSON editor below has invalid JSON — fix it before adding a category.',
        variant: 'destructive' as any,
      });
      return;
    }
    const id = uniqueId(slugify(newCategory.label) || 'new_category', categoryIds);
    const allQuestions: Question[] = [];
    newCategory.questions
      .filter((q) => q.trim())
      .forEach((text) => allQuestions.push({ id: newQuestionId(allQuestions), en: text }));

    const newCategoryObj: Category = {
      id,
      labels: { en: newCategory.label.trim() },
      description: newCategory.description.trim() || undefined,
      persona_name: newCategory.personaName.trim(),
      persona_description: newCategory.mode === 'virtual_close' ? newCategory.personaDescription.trim() : undefined,
      specialty: { en: '' },
      resolution_mode: newCategory.mode,
      default_priority: newCategory.priority,
      sla_response_minutes: newCategory.slaMinutes ? Number(newCategory.slaMinutes) : undefined,
      allow_multiple_open: newCategory.allowMultipleOpen,
      use_ragflow: newCategory.mode === 'virtual_close' ? newCategory.useRagflow : undefined,
      use_mcp_tools: newCategory.mode === 'virtual_close' ? newCategory.useMcpTools : undefined,
      session_delivery_type_id: newCategory.sessionDeliveryTypeId !== 'none' ? Number(newCategory.sessionDeliveryTypeId) : undefined,
      questions: allQuestions,
      active: true,
    };
    const nextConfig = { ...parsedConfig, categories: [...categories, newCategoryObj] };

    setCreatingCategory(true);
    try {
      await persistConfig(nextConfig);
      toast({ title: 'Category added', description: `${newCategory.label.trim()} — saved.` });
      setNewCategory(createBlankDraft());
      setCatView('grid');
    } catch (error: any) {
      // Deliberately does NOT reset the draft or leave the add form on
      // failure — same "keep what they typed, tell them it didn't save"
      // behavior as `save`'s own catch block, so a rejected category isn't
      // silently lost a second way.
      toast({
        title: 'Category was not saved',
        description: error?.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setCreatingCategory(false);
    }
  };

  const loadDatasets = async (categoryId: string) => {
    setDatasetsLoading((prev) => ({ ...prev, [categoryId]: true }));
    try {
      const rows = await digitalCompanionDatasetsAPI.list(categoryId);
      setDatasets((prev) => ({ ...prev, [categoryId]: rows }));
    } catch (error: any) {
      toast({
        title: 'Could not load dataset',
        description: error?.response?.data?.detail,
        variant: 'destructive',
      });
    } finally {
      setDatasetsLoading((prev) => ({ ...prev, [categoryId]: false }));
    }
  };

  // Loads dataset entries the moment a category is opened for editing —
  // replaces the old accordion's toggleExpanded-triggered fetch.
  useEffect(() => {
    if (!editingCategoryId) return;
    if (!datasets[editingCategoryId] && !datasetsLoading[editingCategoryId]) {
      loadDatasets(editingCategoryId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingCategoryId]);

  const handleDatasetUpload = async (categoryId: string, file: File) => {
    setDatasetUploading((prev) => ({ ...prev, [categoryId]: true }));
    try {
      await digitalCompanionDatasetsAPI.import(categoryId, file);
      await loadDatasets(categoryId);
      toast({ title: 'Dataset uploaded' });
    } catch (error: any) {
      toast({
        title: 'Could not upload dataset',
        description: error?.response?.data?.detail || 'Please check the file is valid JSON.',
        variant: 'destructive',
      });
    } finally {
      setDatasetUploading((prev) => ({ ...prev, [categoryId]: false }));
      const input = datasetFileInputs.current[categoryId];
      if (input) input.value = '';
    }
  };

  const handleDatasetDelete = async (categoryId: string, datasetId: number) => {
    try {
      await digitalCompanionDatasetsAPI.remove(categoryId, datasetId);
      setDatasets((prev) => ({
        ...prev,
        [categoryId]: (prev[categoryId] || []).filter((d) => d.id !== datasetId),
      }));
    } catch (error: any) {
      toast({
        title: 'Could not delete dataset entry',
        description: error?.response?.data?.detail,
        variant: 'destructive',
      });
    }
  };

  // §3.8: drafts persona_name + persona_description, fills the two fields
  // for the admin to review/edit — nothing is saved until they hit the
  // page's own Save button, same as every other AI-assist in this app.
  const handleSuggestPersona = async (category: Category, hint: string) => {
    setPersonaSuggesting((prev) => ({ ...prev, [category.id]: true }));
    try {
      const result = await digitalCompanionDatasetsAPI.suggestPersona({
        category_label: category.labels?.en || category.id,
        hint,
        questions: category.questions || [],
        resolution_mode: category.resolution_mode || 'human',
      });
      updateCategory(category.id, {
        persona_name: result.persona_name,
        persona_description: result.persona_description,
      });
    } catch (error: any) {
      toast({
        title: 'Could not draft a persona',
        description: error?.response?.data?.detail,
        variant: 'destructive',
      });
    } finally {
      setPersonaSuggesting((prev) => ({ ...prev, [category.id]: false }));
    }
  };

  // 2026-08-09: drafts 3-5 intake questions for a category, same
  // draft-then-approve pattern as handleSuggestPersona above — appends to
  // whatever questions already exist rather than replacing them, so
  // re-clicking to get more suggestions doesn't wipe ones the admin already
  // edited. Nothing is saved until the page's own Save button is hit.
  const handleSuggestQuestions = async (category: Category, hint: string) => {
    setQuestionsSuggesting((prev) => ({ ...prev, [category.id]: true }));
    try {
      const result = await digitalCompanionDatasetsAPI.suggestQuestions({
        category_label: category.labels?.en || category.id,
        hint,
        resolution_mode: category.resolution_mode || 'human',
        persona_description: category.persona_description || '',
        existing_questions: category.questions || [],
      });
      const existing = category.questions || [];
      // newQuestionId derives an id from the existing-ids list alone (no
      // running counter) — accumulate into `allSoFar` per iteration so each
      // drafted question gets a distinct id instead of all colliding on the
      // same one.
      const allSoFar = [...existing];
      result.questions.forEach((text) => {
        allSoFar.push({ id: newQuestionId(allSoFar), en: text });
      });
      updateCategory(category.id, { questions: allSoFar });
    } catch (error: any) {
      toast({
        title: 'Could not draft questions',
        description: error?.response?.data?.detail,
        variant: 'destructive',
      });
    } finally {
      setQuestionsSuggesting((prev) => ({ ...prev, [category.id]: false }));
    }
  };

  // Add New's "describe it, let AI draft the rest" shortcut — reuses the
  // same suggest-persona / suggest-questions endpoints as the per-field
  // buttons above, called together against one shared hint, rather than
  // needing a new unified backend endpoint.
  const handleDraftNewWithAI = async () => {
    if (!newCategory.hint.trim()) return;
    setDraftingNew(true);
    try {
      const categoryLabel = newCategory.label.trim() || newCategory.hint.trim();
      const [personaResult, questionsResult] = await Promise.all([
        newCategory.mode === 'virtual_close'
          ? digitalCompanionDatasetsAPI.suggestPersona({
              category_label: categoryLabel,
              hint: newCategory.hint,
              resolution_mode: newCategory.mode,
            })
          : Promise.resolve(null),
        digitalCompanionDatasetsAPI.suggestQuestions({
          category_label: categoryLabel,
          hint: newCategory.hint,
          resolution_mode: newCategory.mode,
          existing_questions: [],
        }),
      ]);
      setNewCategory((prev) => ({
        ...prev,
        personaName: personaResult?.persona_name || prev.personaName,
        personaDescription: personaResult?.persona_description || prev.personaDescription,
        questions: questionsResult.questions.length ? questionsResult.questions : prev.questions,
      }));
    } catch (error: any) {
      toast({
        title: 'Could not draft with AI',
        description: error?.response?.data?.detail,
        variant: 'destructive',
      });
    } finally {
      setDraftingNew(false);
    }
  };

  // Shared by the "Save Feature Settings" button and any action (e.g. Add
  // category) that needs to persist immediately rather than leaving it to a
  // separate manual save — pulled out of `save` below so both paths apply
  // the exact same request shape and post-save state sync. `value` must
  // already be loaded (both call sites only run once it is).
  const persistConfig = async (configToSave: Record<string, any>) => {
    const saved = await complaintTicketsAPI.updateConfig({
      enabled: value!.enabled,
      digital_companion_enabled: value!.digital_companion_enabled,
      config: configToSave,
      agent_notify_template_id: value!.agent_notify_template_id || null,
      notify_channel_id: value!.notify_channel_id || null,
    });
    setValue({
      ...saved,
      digital_companion_enabled: saved.digital_companion_enabled ?? true,
    });
    setJsonText(JSON.stringify(editableProductConfig(saved.config), null, 2));
    window.dispatchEvent(new Event('ticketing-feature-flags-changed'));
    return saved;
  };

  const save = async () => {
    if (!value) return;
    const configToSave =
      parsedConfig ||
      (!value.enabled || !value.digital_companion_enabled ? value.config : null);
    if (!configToSave) return;
    setSaving(true);
    try {
      const saved = await persistConfig(configToSave);
      toast({
        title: 'Feature settings saved',
        description: saved.enabled
          ? `Ticketing is enabled ${
              saved.digital_companion_enabled ? 'with' : 'without'
            } Digital Companion.`
          : 'Ticketing and Digital Companion are disabled. Issued Tickets is hidden.',
      });
    } catch (error: any) {
      toast({
        title: 'Settings were not saved',
        description: error?.response?.data?.detail || 'Please verify the configuration.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !value) {
    return (
      <div className="flex min-h-[240px] items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const editingCategory = editingCategoryId ? categories.find((c) => c.id === editingCategoryId) || null : null;
  const editingIndex = editingCategory ? categories.findIndex((c) => c.id === editingCategory.id) : -1;

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader>
          <CardTitle>Feature Flags</CardTitle>
          <CardDescription>
            These account-level changes take effect together only after you save.
          </CardDescription>
        </CardHeader>
        <CardContent className="divide-y">
          <div className="flex items-center justify-between gap-4 py-4 first:pt-0">
            <div>
              <Label htmlFor="ticketing-enabled" className="text-sm font-medium">
                Ticketing
              </Label>
              <p className="mt-1 text-sm text-muted-foreground">
                Enables ticket APIs and the Issued Tickets workspace.
              </p>
            </div>
            <Switch
              id="ticketing-enabled"
              checked={value.enabled}
              onCheckedChange={(enabled) => setValue((prev) => prev ? { ...prev, enabled } : prev)}
              aria-label="Enable Ticketing"
            />
          </div>
          {value.enabled && (
            <div className="flex items-center justify-between gap-4 py-4 last:pb-0">
              <div>
                <Label htmlFor="digital-companion-enabled" className="text-sm font-medium">
                  Digital Companion
                </Label>
                <p className="mt-1 text-sm text-muted-foreground">
                  Adds conversational complaint detection, intake, and ticket-status replies.
                </p>
              </div>
              <Switch
                id="digital-companion-enabled"
                checked={value.digital_companion_enabled}
                onCheckedChange={(digital_companion_enabled) =>
                  setValue((prev) => prev ? { ...prev, digital_companion_enabled } : prev)
                }
                aria-label="Enable Digital Companion"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {value.enabled && value.digital_companion_enabled && (
        <>
          <Card ref={configuredCardRef}>
            <CardHeader>
              <div className="space-y-3">
                <CardTitle className="text-base">Configured categories</CardTitle>
                <div className="flex w-fit gap-1 rounded-lg border bg-muted/40 p-1">
                  <button
                    type="button"
                    onClick={() => setCatView('add')}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
                      catView === 'add' ? 'bg-background text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add new
                  </button>
                  <button
                    type="button"
                    onClick={() => setCatView('grid')}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
                      catView === 'grid' ? 'bg-background text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <LayoutGrid className="h-3.5 w-3.5" />
                    Configured
                    <Badge variant="secondary" className="ml-0.5 px-1.5 py-0 text-[10px]">{categories.length}</Badge>
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {jsonError && (
                <Alert variant="destructive">
                  <TriangleAlert className="h-4 w-4" />
                  <AlertDescription>
                    Configuration error ({jsonError}) — reload the page to reset to the last
                    saved settings.
                  </AlertDescription>
                </Alert>
              )}

              {!jsonError && catView === 'add' && (
                <div className="space-y-5">
                  <button
                    type="button"
                    onClick={() => setCatView('grid')}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-primary"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" /> Back to categories
                  </button>

                  <div>
                    <p className="text-sm font-semibold">Who resolves it?</p>
                    <p className="text-xs text-muted-foreground">
                      Start here — the fields below only ask for what this choice actually needs. Your companion
                      always greets the contact and runs the intake questions either way.
                    </p>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <button
                      type="button"
                      onClick={() => setNewCategory((prev) => ({ ...prev, mode: 'human' }))}
                      className={cn(
                        'rounded-xl border-2 p-5 text-center transition-colors',
                        newCategory.mode === 'human' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40',
                      )}
                    >
                      <UserCheck className="mx-auto h-7 w-7 text-primary" />
                      <p className="mt-2.5 text-sm font-bold">Hands off to a person</p>
                      <p className="mx-auto mt-1 max-w-[240px] text-xs text-muted-foreground">
                        After intake, a teammate on your team takes it from there to resolve it.
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewCategory((prev) => ({ ...prev, mode: 'virtual_close' }))}
                      className={cn(
                        'rounded-xl border-2 p-5 text-center transition-colors',
                        newCategory.mode === 'virtual_close' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40',
                      )}
                    >
                      <Bot className="mx-auto h-7 w-7 text-primary" />
                      <p className="mt-2.5 text-sm font-bold">Companion resolves it directly</p>
                      <p className="mx-auto mt-1 max-w-[240px] text-xs text-muted-foreground">
                        Virtual close — after intake, the companion continues the conversation and can close it
                        out itself.
                      </p>
                    </button>
                  </div>

                  <div className="space-y-2 rounded-lg border p-3">
                    <Label className="flex items-center gap-1.5 text-xs">
                      <Sparkles className="h-3.5 w-3.5" /> Optional: describe it, let AI draft the rest
                    </Label>
                    <Textarea
                      rows={2}
                      placeholder="e.g. Community health workers reporting that a clinic has run out of a specific TB drug — needs to escalate fast."
                      value={newCategory.hint}
                      onChange={(e) => setNewCategory((prev) => ({ ...prev, hint: e.target.value }))}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={draftingNew || !newCategory.hint.trim()}
                      onClick={handleDraftNewWithAI}
                    >
                      {draftingNew ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                      Draft with AI
                    </Button>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Label</Label>
                      <Input
                        placeholder="e.g. Water / Sanitation Complaint"
                        value={newCategory.label}
                        onChange={(e) => setNewCategory((prev) => ({ ...prev, label: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-1 sm:col-span-2">
                      <Label className="text-xs">Description</Label>
                      <p className="text-xs text-muted-foreground">
                        What this category is for — admin-facing only, shown in the category list and
                        synced to the matching Indicator Catalogue entry. Never seen by classification.
                      </p>
                      <Textarea
                        rows={2}
                        placeholder="e.g. Reports about broken or unavailable water/sanitation facilities at a health center."
                        value={newCategory.description}
                        onChange={(e) => setNewCategory((prev) => ({ ...prev, description: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Persona name</Label>
                      <Input
                        placeholder="e.g. Zola"
                        value={newCategory.personaName}
                        onChange={(e) => setNewCategory((prev) => ({ ...prev, personaName: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Default priority</Label>
                      <Select
                        value={newCategory.priority || 'normal'}
                        onValueChange={(v) => setNewCategory((prev) => ({ ...prev, priority: v as Category['default_priority'] }))}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">SLA response target (minutes)</Label>
                      <Input
                        type="number"
                        min={1}
                        placeholder="No SLA"
                        value={newCategory.slaMinutes}
                        onChange={(e) => setNewCategory((prev) => ({ ...prev, slaMinutes: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                    <div>
                      <Label className="text-sm font-normal">Allow multiple open tickets</Label>
                      <p className="text-xs text-muted-foreground">
                        Off by default — a contact with an open ticket here is asked to add to it instead.
                      </p>
                    </div>
                    <Switch
                      checked={newCategory.allowMultipleOpen}
                      onCheckedChange={(checked) => setNewCategory((prev) => ({ ...prev, allowMultipleOpen: checked }))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs">Intake questions</Label>
                    {newCategory.questions.map((question, i) => (
                      <div key={i} className="grid gap-2 md:grid-cols-[1fr_auto]">
                        <Input
                          placeholder="Question"
                          value={question}
                          onChange={(e) =>
                            setNewCategory((prev) => ({
                              ...prev,
                              questions: prev.questions.map((existing, idx) => (idx === i ? e.target.value : existing)),
                            }))
                          }
                        />
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="text-destructive"
                          onClick={() =>
                            setNewCategory((prev) => ({ ...prev, questions: prev.questions.filter((_, idx) => idx !== i) }))
                          }
                          aria-label="Remove question"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setNewCategory((prev) => ({ ...prev, questions: [...prev.questions, ''] }))}
                    >
                      <Plus className="h-4 w-4" /> Add question
                    </Button>
                  </div>

                  {newCategory.mode === 'virtual_close' && (
                    <div className="space-y-4 rounded-md border p-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Persona description</Label>
                        <p className="text-xs text-muted-foreground">
                          Tone, voice, what it should and shouldn't do — feeds the conversation once it's
                          resolving things directly.
                        </p>
                        <Textarea
                          rows={2}
                          placeholder="e.g. Warm, patient-first, avoids clinical jargon, always offers a concrete next step."
                          value={newCategory.personaDescription}
                          onChange={(e) => setNewCategory((prev) => ({ ...prev, personaDescription: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Linked service package</Label>
                        <Select
                          value={newCategory.sessionDeliveryTypeId}
                          onValueChange={(id) => setNewCategory((prev) => ({ ...prev, sessionDeliveryTypeId: id }))}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {deliveryTypes
                              .filter((t) => t.archetype === 'session_visit')
                              .map((t) => (
                                <SelectItem key={t.id} value={String(t.id)}>{t.label}</SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center justify-between gap-4">
                        <Label className="text-sm font-normal">Use RAGFlow knowledge base</Label>
                        <Switch
                          checked={newCategory.useRagflow}
                          onCheckedChange={(checked) => setNewCategory((prev) => ({ ...prev, useRagflow: checked }))}
                        />
                      </div>
                      <div className="flex items-center justify-between gap-4">
                        <Label className="text-sm font-normal">Use MCP tools</Label>
                        <Switch
                          checked={newCategory.useMcpTools}
                          onCheckedChange={(checked) => setNewCategory((prev) => ({ ...prev, useMcpTools: checked }))}
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setNewCategory(createBlankDraft()); setCatView('grid'); }}
                    >
                      Cancel
                    </Button>
                    <Button type="button" onClick={handleCreateCategory} disabled={creatingCategory}>
                      {creatingCategory ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      Add category
                    </Button>
                  </div>
                </div>
              )}

              {!jsonError && catView === 'grid' && editingCategory && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => setEditingCategoryId(null)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-primary"
                    >
                      <ArrowLeft className="h-3.5 w-3.5" /> Back to categories
                    </button>
                    <span className="text-xs text-muted-foreground">id: {editingCategory.id}</span>
                  </div>

                  {editingCategory.active === false && (
                    <Alert className="border-amber-500">
                      <TriangleAlert className="h-4 w-4" />
                      <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
                        <span>
                          Auto-created from a monitoring topic — invisible to real classification until you
                          activate it.
                        </span>
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => updateCategory(editingCategory.id, { active: true })}
                        >
                          Activate
                        </Button>
                      </AlertDescription>
                    </Alert>
                  )}

                  <div className="grid gap-3 rounded-lg border bg-muted/20 p-3 text-xs text-muted-foreground sm:grid-cols-3">
                    <div className="flex items-center gap-1.5">
                      <Search className="h-3.5 w-3.5 shrink-0" />
                      {detectLine(editingCategory)}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <HelpCircle className="h-3.5 w-3.5 shrink-0" />
                      {askLine(editingCategory)}
                    </div>
                    <div className="flex items-center gap-1.5">
                      {editingCategory.resolution_mode === 'virtual_close' ? (
                        <Bot className="h-3.5 w-3.5 shrink-0" />
                      ) : (
                        <UserCheck className="h-3.5 w-3.5 shrink-0" />
                      )}
                      {resolveLine(editingCategory)}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => moveCategory(editingCategory.id, -1)}
                      disabled={editingIndex === 0}
                    >
                      <ChevronUp className="h-4 w-4" /> Move up
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => moveCategory(editingCategory.id, 1)}
                      disabled={editingIndex === categories.length - 1}
                    >
                      <ChevronDown className="h-4 w-4" /> Move down
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => duplicateCategory(editingCategory)}
                    >
                      <Copy className="h-4 w-4" /> Duplicate
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="text-destructive"
                      onClick={() => { deleteCategory(editingCategory.id); setEditingCategoryId(null); }}
                    >
                      <Trash2 className="h-4 w-4" /> Delete
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        navigate(
                          `/automation?new_rule=1&category_id=${encodeURIComponent(editingCategory.id)}`,
                        )
                      }
                    >
                      <Workflow className="h-4 w-4" /> Build automation rule
                    </Button>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Label</Label>
                      <Input
                        value={editingCategory.labels?.en || ''}
                        onChange={(e) =>
                          updateCategory(editingCategory.id, {
                            labels: { ...editingCategory.labels, en: e.target.value },
                          })
                        }
                      />
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs">Persona name</Label>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline disabled:opacity-50"
                          disabled={personaSuggesting[editingCategory.id]}
                          onClick={() => handleSuggestPersona(editingCategory, '')}
                        >
                          {personaSuggesting[editingCategory.id] ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Sparkles className="h-3 w-3" />
                          )}
                          Suggest with AI
                        </button>
                      </div>
                      <Input
                        value={editingCategory.persona_name || ''}
                        onChange={(e) =>
                          updateCategory(editingCategory.id, { persona_name: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Resolution mode</Label>
                      <Select
                        value={editingCategory.resolution_mode || 'human'}
                        onValueChange={(mode) =>
                          updateCategory(editingCategory.id, {
                            resolution_mode: mode as 'human' | 'virtual_close',
                          })
                        }
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="human">Human handoff</SelectItem>
                          <SelectItem value="virtual_close">Virtual close (AI resolves)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Linked service package</Label>
                      <Select
                        value={editingCategory.session_delivery_type_id ? String(editingCategory.session_delivery_type_id) : 'none'}
                        onValueChange={(id) =>
                          updateCategory(editingCategory.id, {
                            session_delivery_type_id: id === 'none' ? undefined : Number(id),
                          })
                        }
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {deliveryTypes
                            .filter((t) => t.archetype === 'session_visit')
                            .map((t) => (
                              <SelectItem key={t.id} value={String(t.id)}>{t.label}</SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      {deliveryTypes.filter((t) => t.archetype === 'session_visit').length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                          No Session/Visit package types configured yet — add one under Service Packages first.
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          Lets a resolved conversation auto-track a delivery, and lets "Start AI session"
                          on Service Packages send someone straight into this category.
                        </p>
                      )}
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Default priority</Label>
                      <Select
                        value={editingCategory.default_priority || 'normal'}
                        onValueChange={(value) =>
                          updateCategory(editingCategory.id, {
                            default_priority: value as Category['default_priority'],
                          })
                        }
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">SLA response target (minutes)</Label>
                      <Input
                        type="number"
                        min={1}
                        placeholder="No SLA"
                        value={editingCategory.sla_response_minutes ?? ''}
                        onChange={(e) =>
                          updateCategory(editingCategory.id, {
                            sla_response_minutes: e.target.value ? Number(e.target.value) : undefined,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Specialty</Label>
                      <Input
                        value={editingCategory.specialty?.en || ''}
                        onChange={(e) =>
                          updateCategory(editingCategory.id, {
                            specialty: { ...editingCategory.specialty, en: e.target.value },
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">Description</Label>
                    <p className="text-xs text-muted-foreground">
                      What this category is for — admin-facing only, shown in the category list and
                      synced to the matching Indicator Catalogue entry. Never seen by classification.
                    </p>
                    <Textarea
                      rows={2}
                      value={editingCategory.description || ''}
                      onChange={(e) =>
                        updateCategory(editingCategory.id, { description: e.target.value })
                      }
                      placeholder="e.g. Reports about broken or unavailable water/sanitation facilities at a health center."
                    />
                  </div>

                  {editingCategory.resolution_mode === 'virtual_close' && (
                    <div className="space-y-1">
                      <Label className="text-xs">Persona description</Label>
                      <p className="text-xs text-muted-foreground">
                        Tone, voice, what it should and shouldn't do — feeds the conversation once it's
                        resolving things directly.
                      </p>
                      <Textarea
                        rows={2}
                        value={editingCategory.persona_description || ''}
                        onChange={(e) =>
                          updateCategory(editingCategory.id, { persona_description: e.target.value })
                        }
                        placeholder="e.g. Warm, patient-first, avoids clinical jargon, always offers a concrete next step."
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Questions</Label>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline disabled:opacity-50"
                          disabled={questionsSuggesting[editingCategory.id]}
                          onClick={() => handleSuggestQuestions(editingCategory, '')}
                        >
                          {questionsSuggesting[editingCategory.id] ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Sparkles className="h-3 w-3" />
                          )}
                          Suggest with AI
                        </button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => addQuestion(editingCategory.id)}
                        >
                          <Plus className="h-4 w-4" /> Add question
                        </Button>
                      </div>
                    </div>
                    {(editingCategory.questions || []).length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        No questions — the intake goes straight to resolution.
                      </p>
                    )}
                    {(editingCategory.questions || []).map((question, qIndex) => (
                      <div key={question.id} className="grid gap-2 rounded-md border p-2 md:grid-cols-[1fr_auto]">
                        <Input
                          placeholder="Question"
                          value={question.en || ''}
                          onChange={(e) =>
                            updateQuestion(editingCategory.id, qIndex, { en: e.target.value })
                          }
                        />
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            onClick={() => moveQuestion(editingCategory.id, qIndex, -1)}
                            disabled={qIndex === 0}
                            aria-label="Move question up"
                          >
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            onClick={() => moveQuestion(editingCategory.id, qIndex, 1)}
                            disabled={qIndex === (editingCategory.questions || []).length - 1}
                            aria-label="Move question down"
                          >
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="text-destructive"
                            onClick={() => removeQuestion(editingCategory.id, qIndex)}
                            aria-label="Remove question"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                    <div>
                      <Label htmlFor={`allow-multi-${editingCategory.id}`} className="text-sm font-normal">
                        Allow multiple open tickets
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Off by default — a contact with an open ticket in this category is
                        asked to add to it instead of starting a duplicate.
                      </p>
                    </div>
                    <Switch
                      id={`allow-multi-${editingCategory.id}`}
                      checked={editingCategory.allow_multiple_open === true}
                      onCheckedChange={(checked) => updateCategory(editingCategory.id, { allow_multiple_open: checked })}
                    />
                  </div>

                  {editingCategory.resolution_mode === 'virtual_close' && (
                    <div className="space-y-3 rounded-md border p-3">
                      <div>
                        <Label className="text-xs">Capabilities</Label>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          Apply once the companion is resolving things directly — the intake questions above are
                          unaffected either way.
                        </p>
                      </div>
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <Label htmlFor={`ragflow-${editingCategory.id}`} className="text-sm font-normal">
                            Use RAGFlow knowledge base
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            Ground replies in the account's shared document KB.
                          </p>
                        </div>
                        <Switch
                          id={`ragflow-${editingCategory.id}`}
                          checked={editingCategory.use_ragflow === true}
                          onCheckedChange={(checked) => updateCategory(editingCategory.id, { use_ragflow: checked })}
                        />
                      </div>
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <Label htmlFor={`mcp-${editingCategory.id}`} className="text-sm font-normal">
                            Use MCP tools
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            Let this agent call the account's configured MCP tools.
                          </p>
                        </div>
                        <Switch
                          id={`mcp-${editingCategory.id}`}
                          checked={editingCategory.use_mcp_tools === true}
                          onCheckedChange={(checked) => updateCategory(editingCategory.id, { use_mcp_tools: checked })}
                        />
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="flex items-center gap-1.5 text-xs">
                        <Database className="h-3.5 w-3.5" /> Dataset
                        <span className="font-normal text-muted-foreground">
                          (this agent's own reference material — separate from RAGFlow)
                        </span>
                      </Label>
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={datasetUploading[editingCategory.id]}
                          onClick={() => datasetFileInputs.current[editingCategory.id]?.click()}
                        >
                          {datasetUploading[editingCategory.id] ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Upload className="h-3.5 w-3.5" />
                          )}
                          Upload JSON
                        </Button>
                        <input
                          ref={(el) => { datasetFileInputs.current[editingCategory.id] = el; }}
                          type="file"
                          accept=".json,application/json"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleDatasetUpload(editingCategory.id, file);
                          }}
                        />
                      </div>
                    </div>
                    {datasetsLoading[editingCategory.id] ? (
                      <div className="flex justify-center py-3">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      </div>
                    ) : (datasets[editingCategory.id] || []).length === 0 ? (
                      <p className="text-xs text-muted-foreground">
                        No dataset entries yet — upload a JSON file (a single object, or an
                        array of objects) to get started.
                      </p>
                    ) : (
                      <div className="space-y-1.5">
                        {(datasets[editingCategory.id] || []).map((entry) => (
                          <div
                            key={entry.id}
                            className="flex items-start justify-between gap-2 rounded-md border p-2 text-xs"
                          >
                            <div className="min-w-0">
                              <p className="truncate font-medium">{entry.title}</p>
                              <p className="truncate text-muted-foreground">
                                {typeof entry.content === 'string'
                                  ? entry.content
                                  : JSON.stringify(entry.content)}
                              </p>
                            </div>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6 shrink-0 text-destructive"
                              onClick={() => handleDatasetDelete(editingCategory.id, entry.id)}
                              aria-label={`Remove ${entry.title}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {!jsonError && catView === 'grid' && !editingCategory && (
                categories.length === 0 ? (
                  <div className="flex flex-col items-center gap-3 py-10 text-center">
                    <p className="text-sm text-muted-foreground">No categories configured yet.</p>
                    <Button type="button" size="sm" onClick={() => setCatView('add')}>
                      <Plus className="h-4 w-4" /> Add your first category
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder="Search categories by name or persona…"
                        value={categorySearch}
                        onChange={(e) => setCategorySearch(e.target.value)}
                        className="pl-8"
                      />
                    </div>
                    {filteredCategories.length === 0 ? (
                      <p className="py-8 text-center text-sm text-muted-foreground">
                        No categories match "{categorySearch}".
                      </p>
                    ) : (
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {filteredCategories.map((category) => (
                          <CategoryGridCard
                            key={category.id}
                            category={category}
                            onOpen={() => setEditingCategoryId(category.id)}
                          />
                        ))}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => setCatView('add')}
                      className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed p-4 text-center text-sm text-muted-foreground hover:border-primary hover:bg-primary/5 hover:text-primary"
                    >
                      <Plus className="h-5 w-5" />
                      <span className="font-medium">Add category</span>
                    </button>
                  </>
                )
              )}
            </CardContent>
          </Card>

        </>
      )}

      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={
            saving ||
            (value.enabled && value.digital_companion_enabled && Boolean(jsonError))
          }
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save Feature Settings
        </Button>
      </div>
    </div>
  );
};

// Compact summary card for the Configured grid — avatar initial, label,
// persona · mode, an Active/Needs review badge, and a three-line glance at
// what the category actually does (Detect / Ask / Resolve), so "which
// categories exist and what do they do" is answerable without opening any
// of them. Clicking opens the full editor in place of the grid.
const CategoryGridCard: React.FC<{ category: Category; onOpen: () => void }> = ({ category, onOpen }) => {
  const isActive = category.active !== false;
  const initial = (category.persona_name || category.labels?.en || '?').trim().charAt(0).toUpperCase() || '?';
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'flex flex-col gap-3 rounded-xl border p-4 text-left transition-colors hover:border-primary/50 hover:bg-muted/30',
        !isActive && 'border-amber-300 bg-amber-50/50 dark:border-amber-900 dark:bg-amber-950/20',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-sm font-semibold text-primary">
            {initial}
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{category.labels?.en || category.id}</p>
            <p className="truncate text-xs text-muted-foreground">
              {category.persona_name || 'Unnamed persona'} · {modeLabel(category.resolution_mode)}
            </p>
          </div>
        </div>
        {isActive ? (
          <Badge variant="outline" className="shrink-0 border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
            Active
          </Badge>
        ) : (
          <Badge variant="outline" className="shrink-0 border-amber-500 text-amber-700 dark:text-amber-400">
            Needs review
          </Badge>
        )}
      </div>
      <div className="space-y-1 border-t pt-2.5 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <Search className="h-3 w-3 shrink-0" />
          {detectLine(category)}
        </div>
        <div className="flex items-center gap-1.5">
          <HelpCircle className="h-3 w-3 shrink-0" />
          {askLine(category)}
        </div>
        <div className="flex items-center gap-1.5">
          {category.resolution_mode === 'virtual_close' ? (
            <Bot className="h-3 w-3 shrink-0" />
          ) : (
            <UserCheck className="h-3 w-3 shrink-0" />
          )}
          {resolveLine(category)}
        </div>
      </div>
    </button>
  );
};

export default DigitalCompanionSettings;
